import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import i18n from '../../../Config/app/i18n';
import errorMapper, { Error, ErrorCodes } from '../../utils/errorMapper';
import { useCaptchaFallback } from './useCaptchaFallback.hook';
import { QueryKey } from '@/Config/api/queryKey';
import { PASSWORD_MIN_LENGTH } from '@/Domain/utils/passwordPolicy';

const DEFAULT_FORM_VALUES = {
  new_password1: '',
  new_password2: '',
  token: '',
  uidb64: '',
  recaptcha_response_v3: '',
};

const createResetPasswordValidator = () =>
  z
    .object({
      new_password1: z
        .string()
        .min(
          PASSWORD_MIN_LENGTH,
          i18n.t('validation:password_min_length', { min: PASSWORD_MIN_LENGTH }),
        ),
      new_password2: z.string().min(1, i18n.t('validation:password_required')),
      recaptcha_response_v3: z.string().optional(),
      recaptcha_response_v2: z.string().optional(),
    })
    .refine((data) => data.new_password1 === data.new_password2, {
      message: i18n.t('validation:match_password'),
      path: ['new_password2'],
    });

export type ResetPasswordForm = z.infer<ReturnType<typeof createResetPasswordValidator>> & {
  token: string;
  uidb64: string;
};

type ResetPasswordTarget = { masked_email: string } | { spent: true };

export const useResetPassword = () => {
  useTranslation('validation');
  const { track } = useAnalytics();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const uidb64 = searchParams.get('uidb64');
  const token = searchParams.get('token');
  const hasCompleteLink = !!uidb64 && !!token;
  const targetQueryKey = [
    QueryKey.accounts.resetPasswordTarget,
    uidb64 ?? '',
    token ?? '',
  ] as const;
  const markTargetSpent = async (): Promise<void> => {
    await queryClient.cancelQueries({ queryKey: targetQueryKey, exact: true });
    queryClient.setQueryData<ResetPasswordTarget>(targetQueryKey, { spent: true });
  };
  // A successful lookup tells users which account the reset link targets.
  const target = useQuery<ResetPasswordTarget, Error<ResetPasswordForm>>({
    queryKey: targetQueryKey,
    enabled: hasCompleteLink,
  });
  // Only INVALID_TOKEN proves a dead link; transient lookup failures leave the form usable.
  const targetErrors = target.error?.response?.data?.errors?.root;
  const isLinkInvalid =
    !hasCompleteLink ||
    (target.data !== undefined && 'spent' in target.data) ||
    (Array.isArray(targetErrors) &&
      targetErrors.some(({ code }) => code === ErrorCodes.INVALID_TOKEN));

  const {
    control,
    register,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    mode: 'onTouched',
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(createResetPasswordValidator()),
  });

  const {
    handleCaptchaError,
    handleSubmissionError,
    escalateToV2,
    handleV2Focus,
    isPreparingSubmission,
    prepareCaptchaSubmission,
    shouldFocusV2,
    useV2,
  } = useCaptchaFallback({
    clearSubmissionErrors: () => {
      clearErrors('root');
      clearErrors('recaptcha_response_v2');
    },
    hasV2Token: () => !!getValues('recaptcha_response_v2'),
    resetV2Token: () => setValue('recaptcha_response_v2', ''),
    setGenericError: () => setError('root', { message: i18n.t('reset-password:error_generic') }),
    setRequiredV2Error: () => setError('recaptcha_response_v2', { type: 'required' }),
  });

  const mutation = useMutation<{ email: string }, Error<ResetPasswordForm>, ResetPasswordForm>({
    mutationKey: [QueryKey.accounts.resetPassword],
    onSuccess: async () => {
      await markTargetSpent();
      track('reset_password_form_success', {
        event_category: 'User reset password',
        event_label: 'reset_password_form',
      });
    },
    onError: async (error) => {
      handleSubmissionError();
      track('reset_password_form_error', {
        event_category: 'User reset password',
        event_label: 'reset_password_form',
      });

      const fieldErrors = error.response?.data?.errors;
      if (
        (error.response?.status ?? 0) >= 500 ||
        !fieldErrors ||
        !Object.keys(fieldErrors).length
      ) {
        setError('root', { message: i18n.t('reset-password:error_generic') });
        return;
      }

      const hasInvalidToken = Object.values(fieldErrors).some((errorsForField) =>
        errorsForField?.some(({ code }) => code === ErrorCodes.INVALID_TOKEN),
      );
      if (hasInvalidToken) {
        await markTargetSpent();
        setError('token', { type: 'manual' });
        return;
      }

      errorMapper(fieldErrors, (field, message) => {
        if (handleCaptchaError(field)) {
          return;
        }

        setError(field, { message });
      });
    },
  });

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!uidb64 || !token) {
      setError('token', { type: 'manual' });
      return;
    }
    const payload = await prepareCaptchaSubmission(data);
    if (!payload) {
      return;
    }

    track('reset_password_submit', {
      event_category: 'User reset password',
      event_label: 'reset_password_form',
    });

    mutation.mutate({
      ...payload,
      token,
      uidb64,
    });
  };

  const onVerify = useCallback(
    (captchaToken: string) => {
      setValue('recaptcha_response_v3', captchaToken);
    },
    [setValue],
  );

  register('token', { required: true });
  register('uidb64', { required: true });
  register('recaptcha_response_v3');

  return {
    control,
    escalateToV2,
    handleV2Focus,
    shouldFocusV2,
    useV2,
    register,
    handleSubmit,
    onSubmit,
    errors,
    onVerify,
    watch,
    isPending: mutation.isPending || isPreparingSubmission,
    isSuccess: mutation.isSuccess,
    targetEmail:
      target.data !== undefined && 'masked_email' in target.data
        ? target.data.masked_email
        : undefined,
    isLinkInvalid,
  };
};
